import LogLevel from '../Contexts/Logging/LogLevel';
import EdapIntegrationProxy from '../modules/EdapIntegrationProxy';
import EdapEventMethods from '../modules/EdapEventMethods';
import Event from '../utils/Event';
import DirectNavigationModel from '../components/rjs/ui/DirectNavigationMenu/DirectNavigationModel';

export default class EdapLog {
    constructor(defaultEventProps = {}, navigationData) {
        this._defaultEventProps = defaultEventProps;
        this.supportedEvents = new Map();
        let dropDownMenuType = null;

        this._getSupportedEvent('WebMenu.onToggleClick').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitNavMenuFormToggled(
                eventProps.tourId,
                EdapEventMethods.navMenuDropdown.toggleMethods.dock
            );
        });

        this._getSupportedEvent('WebMenu.render').subscribe((sender, eventProps) => {
            if (
                eventProps.state.docked &&
                dropDownMenuType !== EdapEventMethods.navMenuDropdown.menuTypes.docked
            ) {
                EdapIntegrationProxy.emitNavMenuPresented(
                    eventProps.tourId,
                    EdapEventMethods.navMenuDropdown.menuTypes.docked
                );
                dropDownMenuType = EdapEventMethods.navMenuDropdown.menuTypes.docked;
            }
        });

        this._getSupportedEvent('DropdownMenu.render').subscribe((sender, eventProps) => {
            let menuType = eventProps.state.isDropdownOpen
                ? EdapEventMethods.navMenuDropdown.menuTypes.listExpanded
                : EdapEventMethods.navMenuDropdown.menuTypes.listCollapsed;
            /**
             * Guard against logging this edap event for current sphere changes.
             *
             * We could allow the current sphere change renders to be emitted; the menu
             * is being updated to show that information. However, we only (at the time
             * of writing) care about the render/presentation of the menu following
             * a change in the form for the menu (docked, expanded, collapsed).
             */
            if (menuType !== dropDownMenuType) {
                EdapIntegrationProxy.emitNavMenuPresented(eventProps.tourId, menuType);
                dropDownMenuType = menuType;
            }
        });

        this._getSupportedEvent('GroupView.render').subscribe(function groupViewRender(
            sender,
            eventProps
        ) {
            let groupStatus = groupViewRender.groupStatus || {};
            if (eventProps.props.group.active && !groupStatus[eventProps.props.group.id]) {
                EdapIntegrationProxy.emitNavMenuGroupPresented(
                    eventProps.tourId,
                    eventProps.props.group.id,
                    eventProps.props.group.groupType,
                    eventProps.props.group.spaces.map((space) => space.spaceType)
                );
            }
            groupStatus[eventProps.props.group.id] = eventProps.props.group.active;
            groupViewRender.groupStatus = groupStatus;
        });

        this._getSupportedEvent('GroupView.onGroupClick').subscribe((sender, eventProps) => {
            if (!eventProps.props.group.active) {
                EdapIntegrationProxy.emitNavMenuGroupSelected(
                    eventProps.tourId,
                    eventProps.props.group.id,
                    eventProps.props.group.groupType
                );
            }
        });

        this._getSupportedEvent('onNavMenuOpen').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitNavMenuFormToggled(
                eventProps.tourId,
                EdapEventMethods.navMenuDropdown.toggleMethods.header
            );
        });

        this._getSupportedEvent('onNavMenuClose').subscribe((sender, eventProps) => {
            let toggleMethod = eventProps.automated
                ? EdapEventMethods.navMenuDropdown.toggleMethods.listCloseInteraction
                : EdapEventMethods.navMenuDropdown.toggleMethods.header;
            EdapIntegrationProxy.emitNavMenuFormToggled(eventProps.tourId, toggleMethod);
        });

        this._getSupportedEvent('TourAction.SELECT_GROUP').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitFloorStart(
                eventProps.state.groupId,
                eventProps.state.tourData.tourId
            );
        });

        this._getSupportedEvent('TourAction.LOAD_SPHERE').subscribe((sender, eventProps) => {
            let originSpaceLabel = null;
            let originSpaceId = null;
            let originGroupId = null;
            let destinationSpaceLabel = null;
            let destinationSpaceId = null;
            let destinationGroupId = null;
            if (navigationData) {
                let originElements = DirectNavigationModel.getActiveElements(
                    navigationData,
                    eventProps.state.previousSphereId
                );
                let destinationElements = DirectNavigationModel.getActiveElements(
                    navigationData,
                    eventProps.state.currentSphereId
                );
                originSpaceLabel = originElements.space
                    ? originElements.space.spaceType
                    : originSpaceLabel;
                originSpaceId = originElements.space ? originElements.space.id : originSpaceId;
                originGroupId = originElements.group ? originElements.group.id : originGroupId;
                destinationSpaceLabel = destinationElements.space
                    ? destinationElements.space.spaceType
                    : destinationSpaceLabel;
                destinationSpaceId = destinationElements.space
                    ? destinationElements.space.id
                    : destinationSpaceId;
                destinationGroupId = destinationElements.group
                    ? destinationElements.group.id
                    : destinationGroupId;
            }

            EdapIntegrationProxy.emitTourNavigation(
                eventProps.state.navigationMethod,
                eventProps.state.previousSphereId,
                eventProps.state.currentSphereId,
                eventProps.state.tourId,
                eventProps.state.navigationOrder,
                originSpaceLabel,
                originSpaceId,
                originGroupId,
                destinationSpaceLabel,
                destinationSpaceId,
                destinationGroupId
            );
        });

        this._getSupportedEvent('TourAction.SPHERE_LOADED').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitSphereVisit(
                eventProps.state.currentSphereId,
                eventProps.state.groupId,
                eventProps.state.tourData.tourId
            );
            EdapIntegrationProxy.emitSpherePresented(
                eventProps.state.currentSphereId,
                eventProps.state.tourId,
                eventProps.state.loadTime,
                eventProps.state.initialImageQuality,
                eventProps.state.imageFormat,
                eventProps.state.isInitialSphere
            );
        });

        this._getSupportedEvent('TourAction.HOTSPOT_DISPLAYED').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitHotSpotDisplayed(
                eventProps.state.currentSphereId,
                eventProps.state.tourId
            );
        });

        this._getSupportedEvent('onClickHelp').subscribe((sender, eventProps) => {
            EdapIntegrationProxy.emitTutorialPresented(eventProps.tourId, 1, false, 'HELP');
        });

        this._getSupportedEvent('onOnboardingPage').subscribe((sender, eventProps) => {
            /**
             * the +1 to pageIndex is to translate from the list index to the
             * readable step/page.
             */
            EdapIntegrationProxy.emitTutorialPresented(
                eventProps.tourId,
                eventProps.pageIndex + 1,
                eventProps.automated,
                eventProps.transitionSource
            );
        });

        this._getSupportedEvent('onOnboardingDone').subscribe((sender, eventProps) => {
            /**
             * the +1 to pageIndex is to translate from the list index to the
             * readable step/page.
             */
            EdapIntegrationProxy.emitTutorialHidden(
                eventProps.tourId,
                eventProps.pageIndex + 1,
                eventProps.automated,
                eventProps.transitionSource
            );
        });
    }

    _getSupportedEvent(eventName) {
        if (!this.supportedEvents.has(eventName)) {
            this.supportedEvents.set(eventName, new Event());
        }
        return this.supportedEvents.get(eventName);
    }

    _log(level: LogLevel, eventName, data = {}) {
        let event = this._getSupportedEvent(eventName);
        let eventProps = {
            ...this._defaultEventProps,
            ...data,
        };
        if (event) {
            event.publish(this, eventProps);
        }
    }

    trace(eventName, data) {
        this._log(LogLevel.TRACE, eventName, data);
    }

    debug(eventName, data) {
        this._log(LogLevel.DEBUG, eventName, data);
    }

    info(eventName, data) {
        this._log(LogLevel.INFO, eventName, data);
    }

    warn(eventName, data) {
        this._log(LogLevel.WARN, eventName, data);
    }

    error(eventName, data) {
        this._log(LogLevel.ERROR, eventName, data);
    }

    fatal(eventName, data) {
        this._log(LogLevel.FATAL, eventName, data);
    }
}
